import { useState } from 'react';
import { HiOutlinePlus, HiOutlineArrowPath, HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import { HiOutlineViewGrid, HiOutlineViewList } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { Modal, Alert } from '../../../commons';
import { usePosts, useSearch } from '../../../../hooks';
import { DAV_APIS } from '../../../../adapters';
import DashboardLoader from '../../sections/Loader/DashboardLoader';
import PostCard from './PostCard';
import { Card } from '../../../ui/Card';
import { Button } from '../../../ui/Button';

const Posts = () => {
  const [selectedCategory, setSelectedCategory] = useState<'posts' | 'language'>('posts');
  const [selectedPost, setSelectedPost] = useState('' as string | null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [refresh, setRefresh] = useState<number | null>(null);
  const { posts, loading } = usePosts(selectedCategory, refresh);
  const [searchTerm, setSearchTerm] = useState('' as string);
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');

  const navigate = useNavigate();

  const handleDeletePost = async () => {
    setIsLoading(true);
    try {
      const res = await DAV_APIS.post.deletePostById(selectedPost as string, selectedCategory);
      if (res.status === 204) {
        Alert({ message: 'Deleted Successfully', variant: 'success' });
      }
      setIsLoading(false);
      setShowModal(false);
      setRefresh(Math.random());
    } catch (error) {
      setIsLoading(false);
      setShowModal(false);
      Alert({ message: 'Failed to delete post', variant: 'error' });
    }
  };

  const postsResults = useSearch(posts, searchTerm);

  const renderContent = () => {
    if (loading) {
      return (
        <div className='flex flex-col items-center justify-center py-20 animate-pulse'>
          <DashboardLoader />
        </div>
      );
    }

    if (postsResults.length > 0) {
      return (
        <div className={`grid gap-6 ${layout === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
          {postsResults.map((post, index) => (
            <div key={post.id} style={{ animationDelay: `${index * 50}ms` }} className='animate-in fade-in slide-in-from-bottom-2 duration-500'>
              <PostCard
                post={post}
                layout={layout}
                isLanguage={selectedCategory === 'language'}
                onEditHandler={() =>
                  navigate(
                    `/dashboard/safari-updates/edit?post=${post.id}&category=${selectedCategory}`,
                  )
                }
                onDeleteHandler={() => {
                  setShowModal(true);
                  setSelectedPost(post.id);
                }}
              />
            </div>
          ))}
        </div>
      );
    }

    return (
      <Card variant='glass' className='py-20 flex flex-col items-center justify-center text-center'>
        <div className='w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4'>
          <HiOutlineMagnifyingGlass className='w-10 h-10 text-gray-400' />
        </div>
        <h3 className='text-lg font-bold text-gray-900 dark:text-white'>No updates found</h3>
        <p className='text-gray-500 dark:text-gray-400 max-w-sm mt-1 px-6'>
          {searchTerm ? 
            `We couldn't find any updates matching "${searchTerm}" in this category.`
            : `There are currently no active updates in the ${selectedCategory} category.`}
        </p>
        {searchTerm && (
          <button 
            type='button'
            onClick={() => setSearchTerm('')}
            className='mt-4 text-primary-600 font-bold hover:underline'
          >
            Clear search
          </button>
        )}
      </Card>
    );
  };

  return (
    <div className='space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700'>
      {/* Page Header */}
      <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight'>
            Safari Updates
          </h1>
          <p className='text-gray-500 dark:text-gray-400 mt-1 max-w-2xl'>
            Manage your blog posts and cultural language updates.
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <Button
            variant='primary'
            onClick={() => navigate('/dashboard/safari-updates/new')}
            leftIcon={<HiOutlinePlus className='w-5 h-5' />}
          >
            New Update
          </Button>
        </div>
      </div>

      {/* Controls Bar */}
      <Card variant='glass' className='p-4 border-none shadow-lg shadow-gray-200/40 dark:shadow-none'>
        <div className='flex flex-wrap items-center gap-4'>
          {/* Search */}
          <div className='relative flex-1 min-w-[240px]'>
            <div className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-400'>
              <HiOutlineMagnifyingGlass className='w-5 h-5' />
            </div>
            <input
              type='text'
              placeholder='Search updates by title...'
              onChange={(e) => setSearchTerm(e.target.value)}
              className='
                w-full pl-11 pr-4 py-2.5 bg-white/50 dark:bg-gray-800/50 
                border border-gray-200 dark:border-gray-700 rounded-xl
                focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                placeholder:text-gray-400 dark:placeholder:text-gray-500
                transition-all duration-200
              '
            />
          </div>

          <div className='flex items-center gap-3 ml-auto'>
            {/* Category Selector */}
            <div className='relative min-w-[160px]'>
              <select
                onChange={(e) => setSelectedCategory(e.target.value as 'posts' | 'language')}
                value={selectedCategory}
                className='
                  w-full px-4 py-2.5 bg-white/50 dark:bg-gray-800/50 
                  border border-gray-200 dark:border-gray-700 rounded-xl
                  appearance-none cursor-pointer focus:ring-2 focus:ring-primary-500/20
                  text-gray-700 dark:text-gray-300 font-medium
                  transition-all duration-200
                '
              >
                <option value='posts'>Standard Posts</option>
                <option value='language'>Language Updates</option>
              </select>
              <div className='absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400'>
                <svg className='w-4 h-4 fill-current' viewBox='0 0 20 20'>
                  <path d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' />
                </svg>
              </div>
            </div>

            {/* Layout Toggle */}
            <div className='flex items-center p-1 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700/50'>
              <button
                type='button'
                onClick={() => setLayout('grid')}
                className={`
                  p-1.5 rounded-md transition-all duration-200
                  ${layout === 'grid' ?
                    'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                  }
                `}
                title='Grid View'
              >
                <HiOutlineViewGrid className='w-5 h-5' />
              </button>
              <button
                type='button'
                onClick={() => setLayout('list')}
                className={`
                  p-1.5 rounded-md transition-all duration-200
                  ${layout === 'list' ?
                    'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                  }
                `}
                title='List View'
              >
                <HiOutlineViewList className='w-5 h-5' />
              </button>
            </div>

            {/* Refresh Button */}
            <button
              type='button'
              onClick={() => setRefresh(Math.random())}
              disabled={loading}
              className={`
                p-2.5 rounded-xl border border-gray-200 dark:border-gray-700
                bg-white/50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400
                hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 dark:hover:text-primary-400
                transition-all duration-200 shadow-sm
                ${loading ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}
              `}
              title='Refresh list'
            >
              <HiOutlineArrowPath className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </Card>

      {/* Content Grid */}
      <div className='min-h-[400px]'>
        {renderContent()}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        size='sm'
        open={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={handleDeletePost}
        loading={isLoading}
        successText='Delete Update'
        loadingText='Deleting...'
      >
        <div className='space-y-3'>
          <h2 className='text-xl font-bold text-gray-900 dark:text-white'>Confirm Deletion</h2>
          <p className='text-gray-600 dark:text-gray-400 leading-relaxed'>
            Are you sure you want to delete this update? This action is permanent and will remove the content from the website.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Posts;
